"use client";

import NextLink from "next/link";

import { HelpModal } from "@/components/homepage/help-modal";
import { HomepageFooter } from "@/components/homepage/homepage-footer";
import { NewsletterFooterBand } from "@/components/homepage/newsletter-footer-band";
import { NewsletterSection } from "@/components/homepage/newsletter-section";
import { useScrollEffects } from "@/components/homepage/use-scroll-effects";
import { PublicSiteHeader } from "@/components/shared/public-site-header";
import {
  CONCIERGE_EMAIL,
  CONCIERGE_WHATSAPP_HREF,
  CONCIERGE_WHATSAPP_LABEL,
} from "@/lib/concierge-contact";
import type { LegalBlock, LegalDocument, LegalRichText } from "@/lib/legal/legal-document";

/**
 * A legal page of the MVP — `Termos e Condicoes.dc.html` and `Politica de Privacidade.dc.html` in
 * the final design package, which draw the same page twice: a porcelain banner with the title, a
 * 760px reading column of numbered sections in the digital body face (Arial, canon §20), the
 * Concierge's channels, the way back and the way across, then the homepage's own closing band.
 *
 * The wording lives in `lib/legal/*-content.ts`; this component only draws it.
 */

/** The DS eyebrow `eyebrow eyebrow--brand` at 15px, as the homepage draws it. */
const EYEBROW_CLASS = "block text-[15px] font-medium uppercase tracking-[0.16em] text-tb-green";

const BODY_TEXT_CLASS = "font-[family-name:var(--font-digital)] text-[15.5px] leading-[1.8] text-[var(--fg-muted)] [text-wrap:pretty]";

const ROW_LINK_CLASS =
  "flex items-center justify-between gap-4 border-b border-[rgb(61_63_57_/_0.15)] py-[18px] text-[15px] text-[var(--fg)] transition-colors duration-[var(--tb-duration-3)] ease-[var(--tb-ease-linen)] hover:text-tb-green focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tb-green focus-visible:ring-offset-2";

const OUTLINE_BUTTON_CLASS =
  "inline-flex items-center gap-3 border border-[var(--fg)] px-7 py-[15px] text-[12px] font-bold uppercase tracking-[0.14em] text-[var(--fg)] transition-[background-color,color] duration-[var(--tb-duration-3)] ease-[var(--tb-ease-linen)] hover:bg-[var(--fg)] hover:text-tb-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tb-green focus-visible:ring-offset-2";

function Rich({ text }: { text: LegalRichText }) {
  return (
    <>
      {text.map((segment, index) =>
        typeof segment === "string" ? (
          segment
        ) : (
          <strong key={index} className="font-medium text-[var(--fg)]">
            {segment.strong}
          </strong>
        ),
      )}
    </>
  );
}

function Block({ block }: { block: LegalBlock }) {
  switch (block.kind) {
    case "lead":
      return (
        <p className="font-[family-name:var(--font-digital)] text-[clamp(16px,1.25vw,18px)] leading-[1.68] text-[var(--fg)] [text-wrap:pretty]">
          <Rich text={block.text} />
        </p>
      );
    case "paragraph":
      return (
        <p className={BODY_TEXT_CLASS}>
          <Rich text={block.text} />
        </p>
      );
    case "subhead":
      return (
        <p className="mt-2 text-[14px] font-medium tracking-[0.02em] text-[var(--fg)]">
          <Rich text={block.text} />
        </p>
      );
    case "note":
      return (
        <p className="border-l-2 border-tb-green pl-3.5 font-[family-name:var(--font-digital)] text-[13.5px] leading-[1.7] text-[var(--fg-muted)] [text-wrap:pretty]">
          <Rich text={block.text} />
        </p>
      );
    case "list":
      return (
        <ul className="flex flex-col gap-[13px]">
          {block.items.map((item, index) => (
            <li
              key={index}
              className="flex items-start gap-[13px] font-[family-name:var(--font-digital)] text-[15.5px] leading-[1.8] text-[var(--fg-muted)]"
            >
              <span aria-hidden="true" className="mt-2.5 size-[5px] flex-none rounded-full bg-tb-green" />
              <span>
                <Rich text={item} />
              </span>
            </li>
          ))}
        </ul>
      );
    case "contact":
      return (
        <div className="mt-0.5 flex flex-col border-t border-[rgb(61_63_57_/_0.15)]">
          <a href={`mailto:${CONCIERGE_EMAIL}`} className={ROW_LINK_CLASS}>
            {CONCIERGE_EMAIL}
            <span aria-hidden="true" className="text-[15px]">
              →
            </span>
          </a>
          <a href={CONCIERGE_WHATSAPP_HREF} target="_blank" rel="noopener noreferrer" className={ROW_LINK_CLASS}>
            WhatsApp {CONCIERGE_WHATSAPP_LABEL}
            <span aria-hidden="true" className="text-[15px]">
              →
            </span>
          </a>
        </div>
      );
  }
}

export interface LegalPageProps {
  document: LegalDocument;
  /** The other legal page, which the design offers beside the way back. */
  crossLink: { href: string; label: string };
}

export function LegalPage({ document, crossLink }: LegalPageProps) {
  const { circleRef, rootRef, setScrollLocked } = useScrollEffects();

  return (
    <HelpModal>
      <div
        ref={rootRef}
        className="tb-entry-fade tb-editorial-body relative overflow-clip bg-tb-white text-[var(--fg)]"
      >
        <PublicSiteHeader homeHref="/" sectionHrefPrefix="/" surface="solid" onScrollLockChange={setScrollLocked} />

        <main>
          <section className="flex min-h-[max(300px,44svh)] items-end justify-center bg-[var(--tb-porcelain-tint)] px-[clamp(20px,4vw,56px)] pb-[clamp(38px,5vw,64px)] pt-[clamp(116px,15vh,176px)] text-center">
            <div className="mx-auto w-full max-w-[1600px]">
              <h1 className="font-display text-[clamp(30px,4.4vw,63px)] font-light uppercase leading-[1.06] tracking-[var(--tracking-display,-0.01em)] text-[var(--fg)] [text-wrap:balance]">
                {document.title} <span className="text-tb-green">{document.titleAccent}</span>
              </h1>
            </div>
          </section>

          <section className="bg-tb-white px-[clamp(20px,4vw,56px)] pb-[clamp(72px,9vw,132px)] pt-[clamp(56px,7vw,104px)]">
            <div className="mx-auto w-full max-w-[760px]">
              <span className={EYEBROW_CLASS}>{document.eyebrow}</span>

              <div className="mt-[30px] flex flex-col gap-[22px]">
                {document.intro.map((block, index) => (
                  <Block key={index} block={block} />
                ))}
              </div>

              <div className="mt-[clamp(44px,5.5vw,72px)] flex flex-col">
                {document.sections.map((section) => (
                  <div
                    key={section.number}
                    data-fade="1"
                    className="flex flex-col gap-6 border-t border-[rgb(61_63_57_/_0.12)] py-[clamp(36px,4.6vw,54px)]"
                  >
                    <div className="flex flex-col gap-3.5">
                      <span className="text-[12px] font-medium uppercase tracking-[0.18em] text-tb-green">
                        {section.number}
                      </span>
                      <h2 className="font-display text-[clamp(20px,2.02vw,26px)] font-light uppercase leading-[var(--tb-typography-line-height-display)] tracking-[var(--tracking-display,-0.01em)] text-[var(--fg)] [text-wrap:pretty]">
                        {section.title}
                      </h2>
                    </div>
                    {section.blocks.map((block, index) => (
                      <Block key={index} block={block} />
                    ))}
                  </div>
                ))}
              </div>

              <div className="mt-[clamp(40px,5vw,64px)] flex flex-wrap gap-3.5">
                <NextLink href="/" className={OUTLINE_BUTTON_CLASS}>
                  <span aria-hidden="true" className="text-[15px]">
                    ←
                  </span>
                  Voltar para a Lista
                </NextLink>
                <NextLink href={crossLink.href} className={OUTLINE_BUTTON_CLASS}>
                  {crossLink.label}
                  <span aria-hidden="true" className="text-[15px]">
                    →
                  </span>
                </NextLink>
              </div>
            </div>
          </section>
        </main>

        <NewsletterFooterBand circleRef={circleRef}>
          <NewsletterSection />
          <HomepageFooter sectionHrefPrefix="/" />
        </NewsletterFooterBand>
      </div>
    </HelpModal>
  );
}
